/**
 * API-key create service (local, non-SDK) — creates/regenerates the merchant's
 * REST API key with an explicit permission scope set, so the merchant can
 * choose the read/write level at the moment of creation.
 *
 * WHY LOCAL (not the SDK): the SDK's ApiKeysService.createApiKeys does not
 * surface the backend's `scopes` field, so this mirrors merchant_config_service.ts,
 * webhook_service.ts and payout_service.ts — a direct Bearer-authed fetch to the
 * same main-api base (REACT_APP_BACKEND_URL, which is identical to the SDK's
 * baseURL). Read of the key stays on the SDK (apiApiKeysService.getApiKeys).
 *
 * AUTH: Bearer access token from localStorage 'client-metadata'.
 *
 * API CONTRACT:
 *   POST /api-key
 *     body: { scopes?: string[] }   // omitted/["*"] → full access; a read-only
 *                                    // preset restricts to the *:read scopes.
 *     → { access_key, secret_key, environment, scopes, created_at }
 *       (secret_key is returned exactly once, at creation)
 *
 * Creating a key when one already exists REGENERATES it server-side (the old key
 * is superseded) — same semantics the SDK path this replaces already relied on.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

// ─── Permission presets ──────────────────────────────────────────────────────
// The wildcard "*" grants everything (present + future). Read-only is the full
// set of *:read scopes in the backend taxonomy (pkg/apikey/scopes.go) — reads
// are strictly independent of writes there, so a read-only key can never create
// a charge, send a payout, issue a refund, or change configuration.

export const SCOPE_FULL_ACCESS = '*';

export const READ_ONLY_SCOPES: string[] = [
    'customers:read',
    'orders:read',
    'payments:read',
    'payouts:read',
    'connect:read',
    'config:read',
    'webhooks:read',
    'webhooks:events:read',
    'gift_cards:read',
    'audit:read'
];

export type PermissionLevel = 'full' | 'read_only';

/** The scope list sent to the backend for a chosen permission level. */
export function scopesForLevel(level: PermissionLevel): string[] {
    return level === 'read_only' ? [...READ_ONLY_SCOPES] : [SCOPE_FULL_ACCESS];
}

/**
 * Human label for a stored scope list — used on the key card and the success
 * modal. Order-independent.
 *   "*"                → Full access
 *   exactly the reads  → Read only
 *   anything else       → Custom (legacy or manually-scoped keys)
 * An empty/undefined list is treated as Full access (the backend default is "*").
 */
export function describeScopes(scopes?: string[]): 'Full access' | 'Read only' | 'Custom' {
    if (!scopes || scopes.length === 0) return 'Full access';
    if (scopes.includes(SCOPE_FULL_ACCESS)) return 'Full access';
    const set = new Set(scopes);
    const isReadOnly =
        scopes.length === READ_ONLY_SCOPES.length && READ_ONLY_SCOPES.every((s) => set.has(s));
    return isReadOnly ? 'Read only' : 'Custom';
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CreateApiKeyResult {
    access_key: string;
    secret_key: string;
    environment?: string;
    scopes?: string[];
    created_at?: string;
}

/** GET /api-key result (no secret — that is only returned once, at creation). */
export interface ApiKeyInfo {
    access_key: string;
    scopes?: string[];
    environment?: string;
    created_at?: string;
    order_webhook?: string;
    refund_webhook?: string;
}

export class ApiKeyApiError extends Error {
    constructor(message: string, public readonly status: number, options?: ErrorOptions) {
        super(message, options);
        this.name = 'ApiKeyApiError';
    }
}

async function throwOnError(response: Response): Promise<never> {
    let message: string | undefined;
    try {
        const errBody = (await response.json()) as { error?: string; message?: string };
        message =
            typeof errBody?.error === 'string'
                ? errBody.error
                : typeof errBody?.message === 'string'
                ? errBody.message
                : undefined;
    } catch {
        // ignore JSON parse failures on error bodies
    }
    throw new ApiKeyApiError(message ?? `HTTP ${response.status}`, response.status);
}

function envQuery(environment?: string): string {
    return environment ? `?environment=${encodeURIComponent(environment)}` : '';
}

// ─── Service ─────────────────────────────────────────────────────────────────
// Each merchant holds at most one active key PER environment (test + live are
// isolated server-side). Pass `environment` to scope get/create/remove to one
// environment; omit it to preserve the legacy environment-agnostic behavior.

export const apiKeyService = {
    /**
     * GET /api-key[?environment=…] — the merchant's active key for the given
     * environment. Returns null when no key exists there (backend replies 204).
     */
    async get(environment?: string): Promise<ApiKeyInfo | null> {
        const response = await fetch(`${BACKEND_URL}/api-key${envQuery(environment)}`, {
            headers: authHeaders()
        });
        if (response.status === 204) return null;
        if (!response.ok) {
            await throwOnError(response);
        }
        const text = await response.text();
        return text ? (JSON.parse(text) as ApiKeyInfo) : null;
    },

    /**
     * POST /api-key — create (or regenerate) the key for the given environment
     * with the given permission scopes. Regeneration is scoped to the same
     * environment server-side (a new test key never revokes the live key). An
     * empty scope list defaults to full access on the backend; we always send an
     * explicit list for clarity.
     */
    async create(scopes: string[], environment?: string): Promise<CreateApiKeyResult> {
        const body: { scopes: string[]; environment?: string } = {
            scopes: scopes.length ? scopes : [SCOPE_FULL_ACCESS]
        };
        if (environment) body.environment = environment;
        const response = await fetch(`${BACKEND_URL}/api-key`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body)
        });
        if (!response.ok) {
            await throwOnError(response);
        }
        return response.json() as Promise<CreateApiKeyResult>;
    },

    /**
     * DELETE /api-key[?environment=…] — revoke the key for that environment only
     * (omitting environment revokes every environment, the legacy behavior).
     */
    async remove(environment?: string): Promise<void> {
        const response = await fetch(`${BACKEND_URL}/api-key${envQuery(environment)}`, {
            method: 'DELETE',
            headers: authHeaders()
        });
        if (!response.ok) {
            await throwOnError(response);
        }
    }
};
